import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  writeBatch,
  increment
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Database Service
 * Helper functions for common Firestore operations
 */

// ==================== USER OPERATIONS ====================

/**
 * Get user profile by UID
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User profile data
 */
export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

/**
 * Update user profile
 * @param {string} userId - User ID
 * @param {Object} data - Profile data to update
 * @returns {Promise<void>}
 */
export const updateUserProfile = async (userId, data) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Get all clients for a trainer
 * @param {string} trainerId - Trainer ID
 * @returns {Promise<Array>} List of clients
 */
export const getTrainerClients = async (trainerId) => {
  try {
    const clientsRef = collection(db, 'users');
    const q = query(
      clientsRef,
      where('role', '==', 'client'),
      where('trainerId', '==', trainerId),
      where('isActive', '==', true)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting trainer clients:', error);
    throw error;
  }
};

// ==================== CLIENT PROFILE OPERATIONS ====================

/**
 * Get client profile with detailed information
 * @param {string} clientId - Client ID
 * @returns {Promise<Object|null>} Client profile data
 */
export const getClientProfile = async (clientId) => {
  try {
    const profileRef = doc(db, 'clientProfiles', clientId);
    const profileSnap = await getDoc(profileRef);

    if (profileSnap.exists()) {
      return { id: profileSnap.id, ...profileSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting client profile:', error);
    throw error;
  }
};

/**
 * Create or update client profile
 * @param {string} clientId - Client ID
 * @param {Object} profileData - Profile data
 * @returns {Promise<void>}
 */
export const saveClientProfile = async (clientId, profileData) => {
  try {
    const profileRef = doc(db, 'clientProfiles', clientId);
    await setDoc(profileRef, {
      ...profileData,
      userId: clientId,
      lastUpdated: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving client profile:', error);
    throw error;
  }
};

// ==================== FOOD DATABASE OPERATIONS ====================

/**
 * Search foods by name
 * @param {string} searchTerm - Search term
 * @param {number} maxResults - Maximum results to return
 * @returns {Promise<Array>} List of matching foods
 */
export const searchFoods = async (searchTerm, maxResults = 20) => {
  try {
    const foodsRef = collection(db, 'foods');
    const searchTermLower = searchTerm.toLowerCase();

    const q = query(
      foodsRef,
      where('searchTerms', 'array-contains', searchTermLower),
      orderBy('isVerified', 'desc'),
      limit(maxResults)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error searching foods:', error);
    throw error;
  }
};

/**
 * Get food by ID
 * @param {string} foodId - Food ID
 * @returns {Promise<Object|null>} Food data
 */
export const getFoodById = async (foodId) => {
  try {
    const foodRef = doc(db, 'foods', foodId);
    const foodSnap = await getDoc(foodRef);

    if (foodSnap.exists()) {
      return { id: foodSnap.id, ...foodSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting food:', error);
    throw error;
  }
};

/**
 * Add new food to database
 * @param {Object} foodData - Food data
 * @param {string} userId - User who is adding the food
 * @returns {Promise<string>} New food ID
 */
export const addFood = async (foodData, userId) => {
  try {
    const foodsRef = collection(db, 'foods');
    const newFoodRef = doc(foodsRef);

    // Create search terms for efficient searching
    const searchTerms = foodData.name
      .toLowerCase()
      .split(' ')
      .filter(term => term.length > 2);

    await setDoc(newFoodRef, {
      ...foodData,
      searchTerms,
      addedBy: userId,
      isVerified: false,
      createdAt: serverTimestamp()
    });

    return newFoodRef.id;
  } catch (error) {
    console.error('Error adding food:', error);
    throw error;
  }
};

// ==================== MEAL LOG OPERATIONS ====================

/**
 * Get meal logs for a user within date range
 * @param {string} userId - User ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<Array>} List of meal logs
 */
export const getMealLogs = async (userId, startDate, endDate) => {
  try {
    const logsRef = collection(db, 'mealLogs');
    const q = query(
      logsRef,
      where('userId', '==', userId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting meal logs:', error);
    throw error;
  }
};

/**
 * Add meal log
 * @param {Object} mealData - Meal log data
 * @returns {Promise<string>} New log ID
 */
export const addMealLog = async (mealData) => {
  try {
    const logsRef = collection(db, 'mealLogs');
    const newLogRef = doc(logsRef);

    await setDoc(newLogRef, {
      ...mealData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return newLogRef.id;
  } catch (error) {
    console.error('Error adding meal log:', error);
    throw error;
  }
};

/**
 * Update meal log
 * @param {string} logId - Log ID
 * @param {Object} mealData - Updated meal data
 * @returns {Promise<void>}
 */
export const updateMealLog = async (logId, mealData) => {
  try {
    const logRef = doc(db, 'mealLogs', logId);
    await updateDoc(logRef, {
      ...mealData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating meal log:', error);
    throw error;
  }
};

/**
 * Delete meal log
 * @param {string} logId - Log ID
 * @returns {Promise<void>}
 */
export const deleteMealLog = async (logId) => {
  try {
    const logRef = doc(db, 'mealLogs', logId);
    await deleteDoc(logRef);
  } catch (error) {
    console.error('Error deleting meal log:', error);
    throw error;
  }
};

// ==================== PROGRESS TRACKING OPERATIONS ====================

/**
 * Get progress entries for a user
 * @param {string} userId - User ID
 * @param {number} limitCount - Number of entries to fetch
 * @returns {Promise<Array>} List of progress entries
 */
export const getProgressEntries = async (userId, limitCount = 50) => {
  try {
    const progressRef = collection(db, 'progressTracking');
    const q = query(
      progressRef,
      where('userId', '==', userId),
      orderBy('date', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting progress entries:', error);
    throw error;
  }
};

/**
 * Add progress entry
 * @param {Object} progressData - Progress data
 * @returns {Promise<string>} New entry ID
 */
export const addProgressEntry = async (progressData) => {
  try {
    const progressRef = collection(db, 'progressTracking');
    const newEntryRef = doc(progressRef);

    await setDoc(newEntryRef, {
      ...progressData,
      createdAt: serverTimestamp()
    });

    return newEntryRef.id;
  } catch (error) {
    console.error('Error adding progress entry:', error);
    throw error;
  }
};

// ==================== WORKOUT PROGRAM OPERATIONS ====================

/**
 * Get workout programs for a trainer
 * @param {string} trainerId - Trainer ID
 * @returns {Promise<Array>} List of programs
 */
export const getTrainerPrograms = async (trainerId) => {
  try {
    const programsRef = collection(db, 'workoutPrograms');
    const q = query(
      programsRef,
      where('trainerId', '==', trainerId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting trainer programs:', error);
    throw error;
  }
};

/**
 * Create workout program
 * @param {Object} programData - Program data
 * @returns {Promise<string>} New program ID
 */
export const createWorkoutProgram = async (programData) => {
  try {
    const programsRef = collection(db, 'workoutPrograms');
    const newProgramRef = doc(programsRef);

    await setDoc(newProgramRef, {
      ...programData,
      usageCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return newProgramRef.id;
  } catch (error) {
    console.error('Error creating workout program:', error);
    throw error;
  }
};

/**
 * Assign program to client
 * @param {string} clientId - Client ID
 * @param {string} programId - Program ID
 * @returns {Promise<void>}
 */
export const assignProgramToClient = async (clientId, programId) => {
  try {
    const batch = writeBatch(db);

    // Update client profile
    const clientRef = doc(db, 'clientProfiles', clientId);
    batch.update(clientRef, {
      currentProgram: programId,
      programStartDate: serverTimestamp(),
      lastUpdated: serverTimestamp()
    });

    // Increment program usage count
    const programRef = doc(db, 'workoutPrograms', programId);
    batch.update(programRef, {
      usageCount: increment(1)
    });

    await batch.commit();
  } catch (error) {
    console.error('Error assigning program to client:', error);
    throw error;
  }
};

// ==================== COMMUNICATION OPERATIONS ====================

/**
 * Get messages between trainer and client
 * @param {string} trainerId - Trainer ID
 * @param {string} clientId - Client ID
 * @param {number} limitCount - Number of messages to fetch
 * @returns {Promise<Array>} List of messages
 */
export const getMessages = async (trainerId, clientId, limitCount = 50) => {
  try {
    const messagesRef = collection(db, 'communications');
    const q = query(
      messagesRef,
      where('trainerId', '==', trainerId),
      where('clientId', '==', clientId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting messages:', error);
    throw error;
  }
};

/**
 * Send message
 * @param {Object} messageData - Message data
 * @returns {Promise<string>} New message ID
 */
export const sendMessage = async (messageData) => {
  try {
    const messagesRef = collection(db, 'communications');
    const newMessageRef = doc(messagesRef);

    await setDoc(newMessageRef, {
      ...messageData,
      isRead: false,
      readAt: null,
      createdAt: serverTimestamp()
    });

    return newMessageRef.id;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Mark message as read
 * @param {string} messageId - Message ID
 * @returns {Promise<void>}
 */
export const markMessageAsRead = async (messageId) => {
  try {
    const messageRef = doc(db, 'communications', messageId);
    await updateDoc(messageRef, {
      isRead: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking message as read:', error);
    throw error;
  }
};

// ==================== FEEDBACK OPERATIONS ====================

/**
 * Get feedback for a trainer
 * @param {string} trainerId - Trainer ID
 * @returns {Promise<Array>} List of feedback
 */
export const getTrainerFeedback = async (trainerId) => {
  try {
    const feedbackRef = collection(db, 'clientFeedback');
    const q = query(
      feedbackRef,
      where('trainerId', '==', trainerId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting trainer feedback:', error);
    throw error;
  }
};

/**
 * Submit client feedback
 * @param {Object} feedbackData - Feedback data
 * @returns {Promise<string>} New feedback ID
 */
export const submitFeedback = async (feedbackData) => {
  try {
    const feedbackRef = collection(db, 'clientFeedback');
    const newFeedbackRef = doc(feedbackRef);

    await setDoc(newFeedbackRef, {
      ...feedbackData,
      isResolved: false,
      trainerResponse: null,
      respondedAt: null,
      createdAt: serverTimestamp()
    });

    return newFeedbackRef.id;
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

// ==================== RECIPE OPERATIONS ====================

/**
 * Get all recipes for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} List of recipes
 */
export const getRecipes = async (userId) => {
  try {
    const recipesRef = collection(db, 'recipes');
    const q = query(
      recipesRef,
      where('coachId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting recipes:', error);
    throw error;
  }
};

/**
 * Add new recipe
 * @param {Object} recipeData - Recipe data
 * @returns {Promise<string>} New recipe ID
 */
export const addRecipe = async (recipeData) => {
  try {
    const recipesRef = collection(db, 'recipes');
    const docRef = await addDoc(recipesRef, {
      ...recipeData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return docRef.id;
  } catch (error) {
    console.error('Error adding recipe:', error);
    throw error;
  }
};

/**
 * Update existing recipe
 * @param {string} recipeId - Recipe ID
 * @param {Object} recipeData - Updated recipe data
 * @returns {Promise<void>}
 */
export const updateRecipe = async (recipeId, recipeData) => {
  try {
    const recipeRef = doc(db, 'recipes', recipeId);
    await updateDoc(recipeRef, {
      ...recipeData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating recipe:', error);
    throw error;
  }
};

/**
 * Delete recipe
 * @param {string} recipeId - Recipe ID
 * @returns {Promise<void>}
 */
export const deleteRecipe = async (recipeId) => {
  try {
    const recipeRef = doc(db, 'recipes', recipeId);
    await deleteDoc(recipeRef);
  } catch (error) {
    console.error('Error deleting recipe:', error);
    throw error;
  }
};

export default {
  getUserProfile,
  updateUserProfile,
  getTrainerClients,
  getClientProfile,
  saveClientProfile,
  searchFoods,
  getFoodById,
  addFood,
  getMealLogs,
  addMealLog,
  updateMealLog,
  deleteMealLog,
  getProgressEntries,
  addProgressEntry,
  getTrainerPrograms,
  createWorkoutProgram,
  assignProgramToClient,
  getMessages,
  sendMessage,
  markMessageAsRead,
  getTrainerFeedback,
  submitFeedback,
  getRecipes,
  addRecipe,
  updateRecipe,
  deleteRecipe
};
